import { ReactNode, useEffect } from 'react';
import { ChevronLeftIcon } from '@/components/Icons';
import { cn } from '@/lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
  hideHeader?: boolean;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  hideHeader = false,
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    full: 'sm:max-w-lg',
  };

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop - only visible on desktop */}
      <div
        className="hidden sm:block fixed inset-0 bg-gray-900/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal - fullscreen on mobile, centered on desktop */}
      <div
        className={cn(
          'fixed inset-0 bg-white flex flex-col',
          'sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:rounded-xl sm:shadow-xl sm:max-h-[85vh]',
          'w-full',
          sizeClasses[size]
        )}
      >
        {/* Safe area top spacer */}
        <div className="flex-shrink-0 safe-top bg-white" />
        
        {/* Header */}
        {!hideHeader && (
          <div className="flex-shrink-0 flex items-center px-2 py-3 border-b border-gray-100 bg-white">
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h3 className="text-xl font-semibold text-gray-900 ml-1">
              {title || ''}
            </h3>
          </div>
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 pb-8">
          {children}
        </div>
        
        {/* Safe area bottom spacer */}
        <div className="flex-shrink-0 safe-bottom bg-white" />
      </div>
    </div>
  );
}
